import { useCallback, useEffect, useRef, useState } from 'react';

// Keep this much of the dialog on screen no matter how far it's dragged, so a
// student can never lose the box (or its drag handle) off the edge.
const KEEP_VISIBLE = 72;

/**
 * Makes a centered dialog draggable by a handle.
 *
 * The dialog stays laid out by its normal flex centering; dragging only applies a
 * translate on top of that, so nothing about the existing layout has to change.
 *
 *   const { boxRef, boxStyle, handleProps, isDragging } = useDraggableDialog(isOpen);
 *   <div ref={boxRef} style={boxStyle}>
 *     <div {...handleProps}>…header…</div>
 *   </div>
 *
 * Pointer events cover mouse, touch and pen with one code path. The offset resets
 * every time the dialog reopens so it always comes back centered.
 */
export default function useDraggableDialog(isOpen) {
  const boxRef = useRef(null);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef(null);

  // Reopening re-centres the dialog rather than restoring wherever it was left.
  useEffect(() => {
    if (isOpen) setOffset({ x: 0, y: 0 });
  }, [isOpen]);

  const onPointerDown = useCallback((e) => {
    // Let buttons, links and inputs inside the handle keep working.
    if (e.target.closest('button, a, input, textarea, select')) return;
    if (e.button != null && e.button !== 0) return;   // primary button / touch only
    const rect = boxRef.current?.getBoundingClientRect();
    if (!rect) return;

    dragRef.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      startOffset: offset,
      // Where the box sits with the current offset already applied.
      rectLeft: rect.left,
      rectTop: rect.top,
      width: rect.width,
      height: rect.height,
    };
    setIsDragging(true);
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) {}
  }, [offset]);

  const onPointerMove = useCallback((e) => {
    const d = dragRef.current;
    if (!d || e.pointerId !== d.pointerId) return;
    e.preventDefault();   // stop the page scrolling under a touch drag

    let dx = e.clientX - d.startX;
    let dy = e.clientY - d.startY;

    // Clamp against the viewport using where the box would land.
    const minLeft = KEEP_VISIBLE - d.width;
    const maxLeft = window.innerWidth - KEEP_VISIBLE;
    const minTop = 0;                                   // never hide the handle above the fold
    const maxTop = window.innerHeight - KEEP_VISIBLE;
    const left = d.rectLeft + dx;
    const top = d.rectTop + dy;
    if (left < minLeft) dx += minLeft - left;
    if (left > maxLeft) dx -= left - maxLeft;
    if (top < minTop) dy += minTop - top;
    if (top > maxTop) dy -= top - maxTop;

    setOffset({ x: d.startOffset.x + dx, y: d.startOffset.y + dy });
  }, []);

  const endDrag = useCallback((e) => {
    const d = dragRef.current;
    if (!d || (e && e.pointerId !== d.pointerId)) return;
    dragRef.current = null;
    setIsDragging(false);
  }, []);

  return {
    boxRef,
    isDragging,
    boxStyle: {
      transform: `translate(${offset.x}px, ${offset.y}px)`,
      // No easing while dragging — the box must track the finger exactly.
      transition: isDragging ? 'none' : undefined,
    },
    handleProps: {
      onPointerDown,
      onPointerMove,
      onPointerUp: endDrag,
      onPointerCancel: endDrag,
      style: {
        cursor: isDragging ? 'grabbing' : 'grab',
        touchAction: 'none',      // required or the browser scrolls instead of dragging
        userSelect: 'none',
      },
    },
  };
}
